import {PartnerLogo, PartnersContainer} from 'components/Partners';

import React, {PropsWithChildren} from 'react';
import styled from 'styled-components';
import googlepay_logo from '@wp-pay/logos/dist/methods/google-pay/method-google-pay-640x360.svg';
import mastercard_logo from '@wp-pay/logos/dist/methods/mastercard/method-mastercard-640x360.svg';
import sepa_logo from 'images/sepa-logo.svg';
import visa_logo from '@wp-pay/logos/dist/methods/visa/method-visa-640x360.svg';
import bizum_logo from 'images/bizum_payment_gateway.svg';
import multibanco_logo from 'images/multibanco_logo.svg';
import dinners_logo from '@wp-pay/logos/dist/cards/diners-club/card-diners-club-logo-_x80.svg';
import jcb_logo from '@wp-pay/logos/dist/cards/jcb/card-jcb-logo-_x80.svg';
import discover_logo from '@wp-pay/logos/dist/cards/discover/card-discover-logo-_x80.svg';
import applepay_logo from '@wp-pay/logos/dist/methods/apple-pay/method-apple-pay-640x360.svg';
import paypal_logo from 'images/paypal_payment_gateway.svg';
import {InternalPageLink} from './links/Pages';
import {ScreenSizes, Sx} from '../../types/responsive';
import {JSX} from 'preact';

export const PaymentMethodsContainer = styled.div`
  display: flex;
  justify-content: space-evenly;
  align-items: center;
  flex-wrap: wrap;
  @media (max-width: ${ScreenSizes.lg}) {
    justify-content: center;
  }

  @media (max-width: ${ScreenSizes.xs}) {
    flex-direction: column;
  }
`;

export const StyledPaymentMethodLogo = styled.div`
  margin: 10px;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 250px;
  width: 250px;
  background: #ffffff;
  box-shadow: ${(props) => props.theme.boxShadow};
  border-radius: ${(props) => props.theme.borderRadius};
  img {
    margin: 0;
  }
  @media (max-width: ${ScreenSizes.md}) {
    height: 230px;
    width: 230px;
  }
`;

type PaymentMethodsProps = {
  andorraPage?: boolean;
  exclude?: string[];
  first?: string[];
  sx?: Sx;
};

export const PaymentMethodLogo: React.FC<React.ImgHTMLAttributes<HTMLImageElement>> = (props) => (
  <StyledPaymentMethodLogo>
    <img {...props} alt="MONEI payment gateway" />
  </StyledPaymentMethodLogo>
);

const PaymentMethod: React.FC<PropsWithChildren<any>> = ({children}) => {
  return <>{children}</>;
};

export const PaymentMethods = ({
  andorraPage,
  exclude = [],
  first = [],
  sx
}: PaymentMethodsProps) => {
  const paymentMethods: {key: string; pm: JSX.Element}[] = [
    ...(!exclude?.includes('visa')
      ? [
          {
            key: 'visa',
            pm: (
              <PartnerLogo
                src={visa_logo}
                alt="Visa payment gateway"
                title="Visa payment gateway"
                width={150}
                sx={sx}
              />
            )
          }
        ]
      : []),
    ...(!andorraPage && !exclude?.includes('paypal')
      ? [
          {
            key: 'paypal',
            pm: (
              <InternalPageLink slug="payment-methods/paypal">
                <PartnerLogo
                  src={paypal_logo}
                  alt="PayPal payment gateway"
                  title="PayPal payment gateway"
                  width={190}
                  sx={sx}
                />
              </InternalPageLink>
            )
          }
        ]
      : []),
    ...(!exclude?.includes('masterCard')
      ? [
          {
            key: 'masterCard',
            pm: (
              <PartnerLogo
                src={mastercard_logo}
                alt="Mastercard payment gateway"
                title="Mastercard payment gateway"
                width={200}
                sx={sx}
              />
            )
          }
        ]
      : []),
    ...(!exclude?.includes('google-pay')
      ? [
          {
            key: 'google-pay',
            pm: (
              <InternalPageLink slug="payment-methods/google-pay">
                <PartnerLogo
                  src={googlepay_logo}
                  alt="Google Pay payment gateway"
                  title="Google Pay payment gateway"
                  width={180}
                  sx={sx}
                />
              </InternalPageLink>
            )
          }
        ]
      : []),
    ...(!exclude?.includes('apple-pay')
      ? [
          {
            key: 'apple-pay',
            pm: (
              <InternalPageLink slug="payment-methods/apple-pay">
                <PartnerLogo
                  src={applepay_logo}
                  alt="Apple Pay payment gateway"
                  title="Apple Pay payment gateway"
                  width={180}
                  sx={sx}
                />
              </InternalPageLink>
            )
          }
        ]
      : []),
    ...(!andorraPage && !exclude?.includes('bizum')
      ? [
          {
            key: 'bizum',
            pm: (
              <InternalPageLink slug="payment-methods/bizum">
                <PartnerLogo
                  src={bizum_logo}
                  alt="Bizum payment gateway"
                  title="Bizum payment gateway"
                  width={170}
                  sx={sx}
                />
              </InternalPageLink>
            )
          }
        ]
      : []),
    ...(!exclude?.includes('multibanco')
      ? [
          {
            key: 'multibanco',
            pm: (
              <InternalPageLink slug="payment-methods/multibanco">
                <PartnerLogo
                  src={multibanco_logo}
                  alt="Multibanco payment gateway"
                  title="Multibanco payment gateway"
                  width={130}
                  sx={sx}
                />
              </InternalPageLink>
            )
          }
        ]
      : []),
    ...(!exclude?.includes('sepa')
      ? [
          {
            key: 'sepa',
            pm: (
              <PartnerLogo
                src={sepa_logo}
                alt="SEPA payment gateway"
                title="SEPA payment gateway"
                width={170}
                sx={sx}
              />
            )
          }
        ]
      : []),
    ...(!exclude?.includes('JBC')
      ? [
          {
            key: 'JBC',
            pm: (
              <PartnerLogo
                src={jcb_logo}
                alt="JCB payment gateway"
                title="JCB payment gateway"
                width={110}
                sx={sx}
              />
            )
          }
        ]
      : []),
    ...(!exclude?.includes('dinners')
      ? [
          {
            key: 'dinners',
            pm: (
              <PartnerLogo
                src={dinners_logo}
                alt="Dinners Club payment gateway"
                title="Dinners Club payment gateway"
                width={180}
                sx={sx}
              />
            )
          }
        ]
      : []),
    ...(!exclude?.includes('discover')
      ? [
          {
            key: 'discover',
            pm: (
              <PartnerLogo
                src={discover_logo}
                alt="Discover payment gateway"
                title="Discover payment gateway"
                width={180}
                sx={sx}
              />
            )
          }
        ]
      : [])
  ];
  return (
    <PartnersContainer sx={sx}>
      {paymentMethods
        .sort((pmA) => (first?.includes(pmA.key) ? -1 : 0))
        .map((paymentMethod, index) => (
          <PaymentMethod key={paymentMethod.key}>{paymentMethod.pm}</PaymentMethod>
        ))}
    </PartnersContainer>
  );
};
